import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Zap, Check, Loader2, Users, Mail, Lock, Clock, AlertTriangle, Activity } from "lucide-react";
import PromoBanner from "@/components/PromoBanner";

const countryToFlag = (code) => {
  const c = (code || "").toUpperCase();
  if (c.length !== 2 || !/^[A-Z]{2}$/.test(c)) return "🌐";
  return String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65,
                              0x1F1E6 + c.charCodeAt(1) - 65);
};
const timeAgo = (iso) => {
  if (!iso) return "";
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    if (diff < 60_000)   return "just now";
    if (diff < 3_600_000)  return `${Math.floor(diff/60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff/3_600_000)}h ago`;
    return `${Math.floor(diff/86_400_000)}d ago`;
  } catch { return ""; }
};

const JustSoldTicker = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const load = () => api.get("/packs/recent-sales?limit=8")
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]));
    load();
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, []);
  if (rows === null) return null;
  if (rows.length === 0) {
    // Honest cold-state line — never fakes a sale
    return (
      <div className="text-center text-[10px] tracking-[0.3em] text-[#5a5050] mt-6 italic"
        data-testid="just-sold-empty">
        <Activity size={10} className="inline mr-1" /> No pack has dropped yet · you could be the first
      </div>
    );
  }
  return (
    <div className="mt-6 py-2 border-y border-[#2d2424] overflow-hidden" data-testid="just-sold">
      <div className="flex items-center gap-6 just-sold-marquee">
        {[...rows, ...rows].map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-[11px] text-[#a39797] whitespace-nowrap flex-shrink-0"
            data-testid={i < rows.length ? `just-sold-row-${i}` : undefined}>
            <Activity size={10} className="text-[#4ade80]" />
            <span className="text-[#5a5050]">{timeAgo(r.paid_at)}</span>
            <span className="text-[#5a5050]">·</span>
            <span>Someone {r.country ? <>in <span className="text-lg leading-none" aria-hidden="true">{countryToFlag(r.country)}</span></> : null} grabbed</span>
            <span className="text-[#d4af37] font-bold">{r.pack_label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/*  /packs — the new 4-tier pricing page.
    Replaces the flat $35 single-case model. Solo / Crew / Chaos / Blackout.
    Checkout → Stripe → webhook flips order to paid → delivery email with
    access code. The /packs/success page handles post-purchase handoff.
*/

const Packs = () => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [checkingOut, setCheckingOut] = useState("");  // pack_id currently loading

  useEffect(() => {
    const load = () => api.get("/packs")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load packs. Refresh and try again."));
    load();
    // Refresh every 30s so the scarcity counter stays current for the FOMO signal
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  const buy = async (packId) => {
    if (!email) {
      toast("Enter your email first — we'll send your access code there.");
      const el = document.querySelector('[data-testid="packs-email"]');
      el?.focus();
      return;
    }
    setCheckingOut(packId);
    try {
      // Capture ?ref= (streamer code or detective share-token) from URL
      const sp = new URLSearchParams(window.location.search);
      const referred_by = (sp.get("ref") || sp.get("r") || "").toUpperCase();
      const { data } = await api.post("/packs/checkout", {
        pack_id: packId,
        email,
        origin_url: window.location.origin,
        referred_by,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Try again.");
      setCheckingOut("");
    }
  };

  return (
    <div className="min-h-screen bg-[#050403] text-[#f4f0ec] pb-24"
      data-testid="packs-page">
      <PromoBanner />
      <div className="border-b border-[#2d2424] bg-[#0a0908]">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="text-xs text-[#a39797] hover:text-[#f4f0ec] flex items-center gap-1.5"
            data-testid="packs-back">
            <ArrowLeft size={14} /> Back
          </Link>
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
            ACCESS PACKS
          </div>
          <div className="w-14" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 pt-10">
        <div className="text-[10px] tracking-[0.4em] text-[#8b0000] mb-3">
          ● INSTANT ACCESS
        </div>
        <h1 className="font-display text-5xl sm:text-6xl text-[#f4f0ec] leading-tight">
          Pick your pack.
        </h1>
        <p className="text-[#a39797] text-sm sm:text-base mt-3">
          Everybody knows something. Nobody knows everything.
          <br className="hidden sm:block" />
          Instant digital delivery · Private self-run lobby · No waiting.
        </p>

        {/* Email capture — kept inline so checkout is one tap */}
        <div className="mt-8 card-noir p-4 bg-black/70 flex items-center gap-3">
          <Mail size={16} className="text-[#d4af37] flex-shrink-0" />
          <input
            type="email" value={email} placeholder="Email for your access code"
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-transparent text-[#f4f0ec] text-sm focus:outline-none"
            data-testid="packs-email"
          />
        </div>

        {/* Pack grid */}
        {!data && (
          <div className="text-center py-14 text-[#5a5050]">
            <Loader2 size={16} className="inline animate-spin mr-2" /> Loading packs…
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6"
              data-testid="packs-grid">
              {data.packs.map((p) => {
                const sc = p.scarcity || {};
                const soldOut = sc.remaining === 0;
                // Fire scarcity signal only when it starts to actually feel scarce
                // (<= 25% remaining). Prevents fake urgency on a fresh week.
                const showScarcity = typeof sc.remaining === "number"
                  && sc.weekly_cap
                  && sc.remaining <= Math.ceil(sc.weekly_cap * 0.25);
                return (
                <div key={p.id}
                  className={`card-noir p-5 flex flex-col relative ${
                    p.highlighted
                      ? "border-[#d4af37] bg-gradient-to-b from-[#d4af37]/5 to-transparent shadow-[0_0_32px_rgba(212,175,55,0.15)]"
                      : "border-[#2d2424]"
                  }`}
                  data-testid={`pack-card-${p.id}`}
                  data-sold-out={soldOut ? "1" : "0"}>
                  {p.badge && !soldOut && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#d4af37] text-black text-[9px] tracking-widest px-2 py-0.5 rounded-full"
                      data-testid={`pack-badge-${p.id}`}>
                      {p.badge}
                    </div>
                  )}
                  {soldOut && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#8b0000] text-[#f4f0ec] text-[9px] tracking-widest px-2 py-0.5 rounded-full"
                      data-testid={`pack-sold-out-${p.id}`}>
                      SOLD OUT · RESETS MON
                    </div>
                  )}
                  <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-1">
                    {p.label.toUpperCase()}
                  </div>
                  {p.sale_price_cents ? (
                    <div className="mt-1">
                      <div className="flex items-baseline gap-2">
                        <div className="font-display text-5xl text-[#d4af37]">
                          {p.sale_price_label}
                        </div>
                        <div className="text-lg text-[#5a5050] line-through font-sans">
                          {p.price_label}
                        </div>
                      </div>
                      <div className="text-[9px] text-[#8b0000] tracking-[0.3em] mt-0.5 font-bold"
                        data-testid={`pack-sale-${p.id}`}>
                        {p.sale_label}
                      </div>
                    </div>
                  ) : (
                    <div className="font-display text-5xl text-[#f4f0ec] mt-1 flex items-baseline gap-1">
                      {p.price_label}
                      <span className="text-xs text-[#5a5050] font-sans">USD</span>
                    </div>
                  )}
                  <div className="text-xs text-[#a39797] mt-1 flex items-center gap-1">
                    <Users size={10} /> {p.tagline}
                  </div>

                  {/* Scarcity line — honest DB-driven. Only shows when actually scarce. */}
                  {showScarcity && (
                    <div className={`mt-3 flex items-center gap-1.5 text-[10px] tracking-widest ${
                      soldOut ? "text-[#f87171]" : "text-[#d4af37]"
                    }`}
                      data-testid={`pack-scarcity-${p.id}`}>
                      <AlertTriangle size={10} />
                      {soldOut
                        ? "SOLD OUT THIS WEEK"
                        : `ONLY ${sc.remaining} LEFT THIS WEEK`}
                    </div>
                  )}

                  <p className="text-xs text-[#c7bebe] mt-3 leading-relaxed">
                    {p.description}
                  </p>

                  {/* Locked case previews — buyers see what they unlock, no spoilers */}
                  {p.preview_cases?.length > 0 && (
                    <div className="mt-4" data-testid={`pack-previews-${p.id}`}>
                      <div className="text-[9px] tracking-[0.3em] text-[#8b0000] mb-2 flex items-center gap-1">
                        <Lock size={9} /> {p.preview_cases.length === 1 ? "YOU UNLOCK" : `UNLOCKS ${p.preview_cases.length} CASES`}
                      </div>
                      <ul className="space-y-1.5">
                        {p.preview_cases.map((c, i) => (
                          <li key={i}
                            className="bg-black/40 border border-[#2d2424] rounded px-2.5 py-2"
                            data-testid={`pack-preview-${p.id}-${i}`}>
                            <div className="flex items-start gap-1.5">
                              <Lock size={10} className="text-[#5a5050] mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-[#f4f0ec] font-bold leading-tight truncate">
                                  {c.title}
                                </div>
                                <div className="text-[10px] text-[#a39797] mt-0.5 leading-snug line-clamp-2">
                                  {c.teaser}
                                </div>
                                <div className="text-[9px] text-[#5a5050] mt-1 tracking-widest">
                                  {c.players} PLAYERS · {c.age}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <ul className="mt-4 space-y-1.5 flex-1" data-testid={`pack-features-${p.id}`}>
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-[#f4f0ec]">
                        <Check size={12} className="text-[#4ade80] mt-0.5 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => buy(p.id)}
                    disabled={checkingOut === p.id || soldOut}
                    className={`mt-5 w-full py-3 text-xs tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      soldOut
                        ? "border border-[#5a5050] text-[#5a5050]"
                        : p.highlighted
                          ? "bg-[#d4af37] text-black hover:bg-[#f4f0ec]"
                          : "border border-[#2d2424] text-[#f4f0ec] hover:border-[#d4af37] hover:text-[#d4af37]"
                    }`}
                    data-testid={`pack-buy-${p.id}`}>
                    {checkingOut === p.id
                      ? <><Loader2 size={12} className="inline animate-spin mr-1" /> OPENING CHECKOUT…</>
                      : soldOut
                        ? <><Clock size={11} className="inline mr-1" /> RESETS MONDAY</>
                        : <><Zap size={11} className="inline mr-1" /> {p.cta.toUpperCase()}</>}
                  </button>
                </div>
              );})}
            </div>

            <div className="text-center text-[10px] tracking-widest text-[#5a5050] mt-6"
              data-testid="packs-scarcity">
              {data.scarcity_line}
            </div>

            {/* Just Sold rolling ticker — pure social proof, no PII */}
            <JustSoldTicker />
          </>
        )}
      </div>
    </div>
  );
};

export default Packs;
